import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { formatGBP, formatDate } from "@/lib/utils-cart";
import { toast } from "sonner";
import { Plus, Briefcase, MessageSquare } from "lucide-react";

export function ProviderServices() {
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = { title: "", category: "accounting", description: "", price_from: 100, pricing_unit: "hour", delivery_time_days: 7 };
  const [form, setForm] = useState(empty);
  const load = () => api.get("/services/mine").then((r) => setServices(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/services", { ...form, price_from: Number(form.price_from), delivery_time_days: Number(form.delivery_time_days) }); toast.success("Service published"); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <DashboardLayout title="My Services" subtitle="Listings shown in the Business Services Marketplace."
      actions={<button onClick={() => setOpen(!open)} className="v-btn-primary" data-testid="service-add"><Plus size={14} /> New service</button>}
    >
      {open && (
        <form onSubmit={create} className="v-card mb-4 space-y-3">
          <input className="v-input" placeholder="Title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required data-testid="service-title" />
          <select className="v-input" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} data-testid="service-category">
            {["accounting","payroll","marketing","legal","insurance","cybersecurity","it_services","erp_consulting","hr_services"].map((c) => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
          </select>
          <textarea rows={3} className="v-input" placeholder="Describe the service" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} required data-testid="service-description" />
          <div className="grid grid-cols-3 gap-2">
            <input type="number" step="0.01" placeholder="Price from" className="v-input" value={form.price_from} onChange={(e) => setForm({...form, price_from: e.target.value})} required data-testid="service-price" />
            <select className="v-input" value={form.pricing_unit} onChange={(e) => setForm({...form, pricing_unit: e.target.value})} data-testid="service-unit">
              <option value="hour">/hour</option>
              <option value="project">/project</option>
              <option value="month">/month</option>
            </select>
            <input type="number" placeholder="Delivery days" className="v-input" value={form.delivery_time_days} onChange={(e) => setForm({...form, delivery_time_days: e.target.value})} data-testid="service-delivery" />
          </div>
          <div className="flex gap-2"><button className="v-btn-primary" data-testid="service-save">Publish</button><button type="button" onClick={() => setOpen(false)} className="v-btn-outline">Cancel</button></div>
        </form>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {services.map((s) => (
          <div key={s.id} className="v-card">
            <div className="label-eyebrow text-secondary">{s.category.replace("_", " ")}</div>
            <h3 className="font-display text-lg font-semibold text-primary mt-2">{s.title}</h3>
            <p className="mt-2 text-sm text-muted-foreground">{s.description}</p>
            <div className="mt-4 pt-4 border-t border-border text-sm flex justify-between">
              <span className="font-mono font-semibold">{formatGBP(s.price_from)} / {s.pricing_unit}</span>
              <span className="text-muted-foreground">{s.delivery_time_days}d delivery</span>
            </div>
          </div>
        ))}
        {services.length === 0 && <div className="col-span-3 p-10 text-center text-muted-foreground v-card">No services listed yet.</div>}
      </div>
    </DashboardLayout>
  );
}

export function Leads() {
  const [leads, setLeads] = useState([]);
  const [responding, setResponding] = useState(null);
  const [form, setForm] = useState({ message: "", quote: "" });
  const load = () => api.get("/leads").then((r) => setLeads(r.data));
  useEffect(() => { load(); }, []);

  const respond = async (e) => {
    e.preventDefault();
    try { await api.post(`/leads/${responding.id}/respond`, { lead_id: responding.id, message: form.message, quote: Number(form.quote) }); toast.success("Quote sent"); setResponding(null); setForm({ message: "", quote: "" }); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <DashboardLayout title="Lead Marketplace" subtitle="Open requests from buyers. Submit quotes to win business.">
      <div className="grid md:grid-cols-2 gap-3">
        {leads.map((l) => (
          <div key={l.id} className="v-card">
            <div className="label-eyebrow text-secondary">{l.category?.replace("_", " ")}</div>
            <div className="mt-1 text-xs text-muted-foreground">{formatDate(l.created_at)} · from {l.requester_name}</div>
            <p className="mt-3 text-sm">{l.description}</p>
            <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
              {l.budget && <span>Budget {formatGBP(l.budget)}</span>}
              {l.timeline && <span>{l.timeline}</span>}
              <span>{l.responses?.length || 0} responses</span>
            </div>
            <button onClick={() => setResponding(l)} className="v-btn-outline mt-4" data-testid={`lead-respond-${l.id}`}>
              <MessageSquare size={14} /> Submit quote
            </button>
          </div>
        ))}
        {leads.length === 0 && <div className="col-span-2 p-10 text-center text-muted-foreground v-card">No open leads right now.</div>}
      </div>

      {responding && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setResponding(null)}>
          <form onSubmit={respond} className="bg-card max-w-lg w-full rounded-sm border border-border p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-display text-xl font-bold text-primary">Submit your quote</h2>
            <div className="mt-4 space-y-3">
              <input type="number" step="0.01" placeholder="Your quote (GBP)" className="v-input" value={form.quote} onChange={(e) => setForm({...form, quote: e.target.value})} required data-testid="lead-quote-amount" />
              <textarea rows={4} placeholder="Your pitch / proposal" className="v-input" value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} required data-testid="lead-quote-message" />
              <div className="flex gap-2"><button className="v-btn-primary" data-testid="lead-quote-submit">Send quote</button><button type="button" onClick={() => setResponding(null)} className="v-btn-outline">Cancel</button></div>
            </div>
          </form>
        </div>
      )}
    </DashboardLayout>
  );
}
